import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, take } from 'rxjs';
import { AUTH_UI_CONFIG, AuthUiConfig } from '../../auth-ui.config';
import { FieldErrors, LoginAlertVM } from './login.vm';
import {
    SESSIONS_PORT,
    SessionsPort,
    NetworkStatusService,
    LoginResponse,
    LoginRequestPlatformEnum,
    isAccountDisabledError,
    accountDisabledMessage,
} from 'lib-klab-core';
import { resolveRedirectIam } from '../../login.helpers';
import { hasAdminPortalAccess } from '../../admin-portal-access';

@Component({
    selector: 'klab-auth-login-page',
    standalone: false,
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
})
export class LoginPageComponent {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);

    private readonly networkStatus = inject(NetworkStatusService);
    private readonly cfg = inject(AUTH_UI_CONFIG) as AuthUiConfig;

    private readonly sessionsPort = inject(SESSIONS_PORT) as SessionsPort;

    loginErrors: FieldErrors = { username: null, password: null };
    alert: LoginAlertVM | null = this.route.snapshot.queryParamMap.get('reason') === 'portal-access'
        ? this.portalAccessAlert() : null;

    private portalAccessAlert(): LoginAlertVM {
        return {
            variant: 'error',
            icon: 'lock',
            title: 'Ups… no es por aquí',
            message: 'Parece que tu cuenta corresponde a otra aplicación. Por favor, inicia sesión en el portal adecuado.',
        };
    }

    onClearLoginErrors(): void {
        this.loginErrors = { username: null, password: null };
        this.alert = null;
    }

    isSubmitLoading = false;
    serviceError: any = null;

    onSubmit(formValue: { username: string; password: string }) {
        this.isSubmitLoading = true;
        this.serviceError = null;
        this.loginErrors = { username: null, password: null };
        this.alert = null;

        if (!this.networkStatus.isOnline()) {
            this.alert = {
                variant: 'server-error',
                icon: 'server',
                title: 'Sin conexión',
                message: 'No se pudo conectar. Verifica tu internet',
            };
            this.isSubmitLoading = false;
            return;
        }

        const payload = {
            email: formValue.username,
            password: formValue.password,
            platform: LoginRequestPlatformEnum.Web,
        };

        this.sessionsPort.login(payload).pipe(
            take(1),
            finalize(() => (this.isSubmitLoading = false))
        ).subscribe({
            next: (session) => this.handleLoginSuccess(session),
            error: (err) => this.handleLoginError(err),
        });
    }

    private handleLoginSuccess(session: LoginResponse) {
        const isAdminLogin = this.cfg.appKind === 'admin';
        const allowed = this.cfg.canAccessPortal?.(session) ?? (!isAdminLogin || hasAdminPortalAccess(session?.menu));
        if (!allowed) {
            localStorage.setItem('IGNORE_NEXT_LOGOUT_REDIRECT', '1');

            this.sessionsPort.logout().pipe(take(1)).subscribe({ complete: () => { } });

            this.alert = this.portalAccessAlert();
            return;
        }

        const redirectFromGuard = this.route.snapshot.queryParamMap.get('redirect');

        const redirect = resolveRedirectIam({
            session,
            cfg: this.cfg,
            redirectFromGuard,
        });

        this.router.navigateByUrl(redirect);
    }

    private handleLoginError(err: any) {
        this.serviceError = err;

        if (err?.status === 401) {
            this.loginErrors = { username: null, password: null };
            this.alert = {
                variant: 'error',
                icon: 'intentos',
                title: 'Ingreso incorrecto',
                message: 'Credenciales incorrectas. Por favor, verifica tus datos.',
            };
            return;
        }

        // Antes que el 403 generico: una cuenta cesada o suspendida llega con
        // el mismo status que el bloqueo por intentos, y solo el codigo las
        // distingue. Sin esta rama, a un cesado se le decia que reintentara en
        // 15 minutos, algo que nunca va a destrabar su cuenta.
        if (isAccountDisabledError(err)) {
            this.loginErrors = { username: null, password: null };
            this.alert = {
                variant: 'error',
                icon: 'lock',
                title: 'Cuenta deshabilitada',
                message: accountDisabledMessage(err),
            };
            return;
        }

        if (err?.status === 403) {
            const backendMessage = err?.error?.message;
            this.alert = {
                variant: 'error',
                icon: 'lock',
                title: 'Acceso denegado',
                message: typeof backendMessage === 'string' && backendMessage.trim()
                    ? backendMessage.trim()
                    : 'Intenta en 15 minutos o comunícate con tu administrador.',
            };
            return;
        }

        this.alert = {
            variant: 'server-error',
            icon: 'server',
            title: 'Error del sistema.',
            message: 'Ocurrió un error en el sistema. Por favor intenta nuevamente en unos minutos.',
        };
    }
}
